import { IdeaSummaryCard } from './idea-summary-card.tsx'
import { useTranslation } from 'react-i18next'
import React from 'react'
import Row from 'antd/es/grid/row'
import Tag from 'antd/es/tag/index'
import Spin from 'antd/es/spin/index'
import IdeyaTypography from '../../../../common/antd/ideya-typography.tsx'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import { selectUser } from '../../../../features/user/user.slice.ts'
import { AccountCategory } from '../../../../enums/management-input.enum.ts'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import { compactNumberFormatter } from '../../../../utils/formatters.ts'
import { SummaryInfoHelp } from './idea-list-summary-info-help.tsx'

interface IdeaSummaryNetExposureProps {
  shouldShowSelectContributorMsg: boolean
  isLoading?: boolean
  netExposureOnGmv: number
  lmv: number
  smv: number
  groupName: string
}

export const IdeaSummaryNetExposure: React.FC<IdeaSummaryNetExposureProps> = ({
  shouldShowSelectContributorMsg,
  isLoading,
  netExposureOnGmv,
  lmv,
  smv,
  groupName,
}) => {
  // const { t } = useTranslation()
  const userInfo = useAppSelector(selectUser)
  const isGlobal = userInfo.region === 'Global'
  const threshold = getNetExposureThreshold(groupName || '')
  const isBreach =
    groupName === AccountCategory.GreaterChinaLongBias
      ? netExposureOnGmv < threshold
      : Math.abs(netExposureOnGmv) > threshold

  const overlayComponent = shouldShowSelectContributorMsg ? (
    <SummaryInfoHelp />
  ) : isLoading ? (
    <Spin />
  ) : null

  return (
    <IdeaSummaryCard
      title={'Net on GMV%'}
      overlayMsg={overlayComponent}
      textComponent={
        <TextComponent
          isBreach={isBreach}
          netExposureOnGmv={netExposureOnGmv}
        />
      }
      slotComponent={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
          }}
        >
          <NetExposureGauge isBreach={isBreach} />
        </div>
      }
      rows={[
        {
          label: 'LMV',
          value: compactNumberFormatter({ value: lmv } as ValueFormatterParams),
        },
        {
          label: 'SMV',
          value: compactNumberFormatter({ value: smv } as ValueFormatterParams),
        },
      ]}
    >
      {!isGlobal && (
        <IdeyaTypography.Text type={'secondary'} size={'sm'}>
          {getNetExposureText(groupName, threshold)}
        </IdeyaTypography.Text>
      )}
    </IdeaSummaryCard>
  )
}

const TextComponent = ({
  isBreach,
  netExposureOnGmv,
}: {
  isBreach: boolean
  netExposureOnGmv: number
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const labelColor = isBreach ? '#FF4D4F' : themeStyle.colorText

  return (
    <div>
      <Row align={'middle'}>
        <IdeyaTypography.Title
          level={3}
          style={{ marginBottom: 0, color: labelColor }}
        >
          {netExposureOnGmv > 0 ? '+' : ''}
          {(netExposureOnGmv * 100).toFixed(2)}%
        </IdeyaTypography.Title>
      </Row>
    </div>
  )
}

const NetExposureGauge = ({ isBreach }: { isBreach: boolean }) => {
  const { t } = useTranslation()

  if (!isBreach) {
    return null
  }

  const mainColor = isBreach ? 'volcano' : 'green'
  const labelText = isBreach
    ? t('idea_list.summary_info.breach')
    : t('idea_list.summary_info.safe')

  return (
    <Tag
      color={mainColor}
      style={{
        fontSize: '1rem',
        fontWeight: 'bold',
        lineHeight: '1.5rem',
        width: '4.1875rem',
        height: '2rem',
        borderWidth: 2,
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {labelText}
    </Tag>
  )
}

function getNetExposureThreshold(groupName: string): number {
  switch (groupName) {
    case AccountCategory.GreaterChinaLowNet:
    case AccountCategory.India:
      return 0.15
    case AccountCategory.GreaterChinaLongBias:
      return 0.7
    case AccountCategory.Japan:
      return 0.15
    case AccountCategory.Korea:
      return 0.25
    default:
      return 1
  }
}

function getNetExposureText(groupName: string, threshold: number): string {
  if (groupName === AccountCategory.GreaterChinaLongBias) {
    return `Within total ≥ ${threshold * 100}% GMV`
  }
  return `Within total ±${threshold * 100}% GMV`
}
